import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { DesignSystemGalleryItem } from 'designSystem/types';
import { buildTabId } from 'designSystem/util';
import useScrollMemory from 'hooks/useScrollMemory';
import GalleryPreview from './GalleryPreview';
import GalleryPage from './GalleryPage';
import { addDsTab, setActiveDsTab, toggleDsPick } from 'redux/mode/actions';
import * as Base from '../styles';
import * as GalleryS from './styles';

const GalleryView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, activeTab, selectedDsPicks } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeTab: selectActiveDsTab(r.mode),
			selectedDsPicks: r.mode.selectedDsPicks,
		}),
		shallowEqual
	);

	// Get contentPath from active tab
	const contentPath = activeTab?.contentPath;
	// Delay restore so iframe previews can resize and change layout
	const scrollRef = useScrollMemory(activeTab?.id ?? null, 400);

	const galleryEntries = designSystemContent?.gallery || [];

	const rawCode = designSystemContent?.rawCode || {
		theme: '',
		styles: '',
		componentsCss: '',
		componentsJs: '',
	};

	const config = designSystemContent?.config;
	const assets = designSystemContent?.assets?.all || [];

	// Check if contentPath points to a specific gallery entry (detail view)
	const detailEntry = useMemo(() => {
		if (contentPath && galleryEntries) {
			return galleryEntries.find((entry) => entry.path === contentPath);
		}
		return null;
	}, [contentPath, galleryEntries]);

	// Navigate to gallery entry detail page on title click
	const onEntryClick = (entry: DesignSystemGalleryItem) => {
		const tabId = buildTabId('gallery', entry.path);
		dispatch(
			addDsTab({
				id: tabId,
				kind: 'gallery',
				contentPath: entry.path,
				label: entry.title,
				showBackButton: true,
			})
		);
		dispatch(setActiveDsTab(tabId));
	};

	// Render detail view if contentPath points to a specific gallery entry
	if (detailEntry) {
		return (
			<GalleryPage
				galleryEntry={detailEntry}
				rawCode={rawCode}
				showBackButton={activeTab?.showBackButton}
			/>
		);
	}

	// Otherwise render the Complete Gallery (stacked list view)
	if (galleryEntries.length === 0) {
		return (
			<Base.SectionBase ref={scrollRef}>
				<Base.SectionHeader>
					<Base.SectionTitle>Gallery</Base.SectionTitle>
				</Base.SectionHeader>
				<Base.SectionContent>
					<GalleryS.EmptyState>No gallery entries found.</GalleryS.EmptyState>
				</Base.SectionContent>
			</Base.SectionBase>
		);
	}

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionContent>
				<GalleryS.CompleteGalleryContainer>
					{galleryEntries.map((entry) => {
						const isSelected = selectedDsPicks.some(
							(p) => p.kind === 'gallery' && p.galleryPath === entry.path
						);
						return (
							<GalleryS.GalleryEntryRow key={entry.path}>
								<GalleryS.GalleryEntryTitleRow>
									<GalleryS.GalleryEntryTitle onClick={() => onEntryClick(entry)}>
										{entry.title}
									</GalleryS.GalleryEntryTitle>
									<Base.SelectToggle
										$selected={isSelected}
										onClick={() =>
											dispatch(toggleDsPick({ kind: 'gallery', galleryPath: entry.path }))
										}
									>
										{isSelected ? 'Selected' : 'Select'}
									</Base.SelectToggle>
								</GalleryS.GalleryEntryTitleRow>
								<GalleryS.GalleryEntryPreview>
							<GalleryPreview
								storyHtml={entry.code}
								isValidHtml={entry.isValidHtml}
								rawCode={rawCode}
								config={config}
								assets={assets}
								autoHeight
							/>
								</GalleryS.GalleryEntryPreview>
							</GalleryS.GalleryEntryRow>
						);
					})}
				</GalleryS.CompleteGalleryContainer>
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default GalleryView;
